import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { AuthLayout } from "../components/shell.tsx";
import { Alert, Button, Panel, PasswordField, Skeleton, TextField } from "../components/ui.tsx";
import { ApiError, applyFieldErrors, post } from "../lib/api.ts";
import { useSession } from "../lib/session.tsx";
import { passwordSchema, personNameSchema, type SessionDto, PASSWORD_HINT } from "../../shared/auth.ts";
import { formatDateBR } from "../../shared/dates.ts";
import { roleLabel, type Role } from "../lib/types.ts";
import { usePageTitle } from "../lib/nav.tsx";

interface Preview {
  plan: { name: string; institution_name: string; starts_on: string; ends_on: string };
  invited_by: string;
  email: string;
  role: Role;
  groups: string[];
  account_exists: boolean;
}

const newAccount = z
  .object({ name: personNameSchema, password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "As senhas não conferem." });

export function InvitationPage({ token }: { token: string }) {
  usePageTitle("Convite para oficina");
  const { user, loading, adopt, logout } = useSession();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  // Preview is a POST (token in the body, never in a query string) and has no side effects.
  const preview = useQuery({
    queryKey: ["invitation-preview", token, user?.id ?? "anon"],
    queryFn: async () => (await post<{ data: Preview }>("/invitations/preview", { token })).data,
    enabled: !loading,
    retry: false,
    staleTime: Infinity,
  });
  const form = useForm<z.input<typeof newAccount>>({ resolver: zodResolver(newAccount), defaultValues: { name: "", password: "", confirm: "" } });

  async function accept(body: { name?: string; password?: string }) {
    setError(null);
    setAccepting(true);
    try {
      const res = await post<{ data: SessionDto & { plan_id: string } }>("/invitations/accept", { token, ...body });
      adopt(res.data);
      navigate(`/planos/${res.data.plan_id}/painel`, { replace: true });
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível aceitar o convite.");
    } finally {
      setAccepting(false);
    }
  }

  let content;
  if (loading || preview.isPending) content = <Skeleton lines={4} />;
  else if (preview.isError) {
    const e = preview.error;
    content =
      e instanceof ApiError && e.code === "INVALID_INVITATION" ? (
        <Alert tone="warn" title="Convite indisponível">{e.message}</Alert>
      ) : (
        <Alert tone="danger" action={<Button variant="secondary" onClick={() => void preview.refetch()}>Tentar novamente</Button>}>
          {e instanceof Error ? e.message : "Não foi possível abrir o convite."}
        </Alert>
      );
  } else {
    const p = preview.data;
    const wrongAccount = user && user.email !== p.email;
    content = (
      <>
        <dl className="mt-3 space-y-1 rounded-ctl bg-paper px-4 py-3 text-sm">
          <div><dt className="inline font-semibold">Oficina: </dt><dd className="inline">{p.plan.name}</dd></div>
          <div><dt className="inline font-semibold">Instituição: </dt><dd className="inline">{p.plan.institution_name}</dd></div>
          <div><dt className="inline font-semibold">Período: </dt><dd className="inline">{formatDateBR(p.plan.starts_on)} a {formatDateBR(p.plan.ends_on)}</dd></div>
          <div><dt className="inline font-semibold">E-mail convidado: </dt><dd className="inline">{p.email}</dd></div>
          <div><dt className="inline font-semibold">Papel: </dt><dd className="inline">{roleLabel[p.role]}</dd></div>
          {p.groups.length > 0 && <div><dt className="inline font-semibold">Grupo: </dt><dd className="inline">{p.groups.join(", ")}</dd></div>}
          <div><dt className="inline font-semibold">Convidado por: </dt><dd className="inline">{p.invited_by}</dd></div>
        </dl>
        {error && <Alert tone="danger">{error}</Alert>}
        {wrongAccount ? (
          <Alert tone="warn" title="Conta diferente" action={<Button variant="secondary" onClick={() => void logout(`/convite/${token}`)}>Sair desta conta</Button>}>
            Você está conectado como {user.email}. Este convite é para {p.email}. Saia e entre com o e-mail convidado.
          </Alert>
        ) : user ? (
          <Button className="mt-4 w-full" loading={accepting} onClick={() => void accept({})}>Aceitar convite</Button>
        ) : p.account_exists ? (
          <>
            <p className="mt-4 text-sm">Já existe uma conta para {p.email}. Entre com ela para aceitar o convite.</p>
            <Link href={`/login?next=${encodeURIComponent(`/convite/${token}`)}`} className="mt-3 inline-flex w-full justify-center rounded-ctl bg-primary px-4 py-2 text-sm font-semibold text-white">
              Entrar para aceitar
            </Link>
          </>
        ) : (
          <form noValidate className="mt-2" onSubmit={form.handleSubmit((v) => accept({ name: v.name, password: v.password }))}>
            <p className="mt-3 text-sm">Crie sua conta individual para participar.</p>
            <TextField label="Nome" autoComplete="name" error={form.formState.errors.name?.message} {...form.register("name")} />
            <TextField label="E-mail" value={p.email} readOnly />
            <PasswordField label="Senha" autoComplete="new-password" hint={PASSWORD_HINT} error={form.formState.errors.password?.message} {...form.register("password")} />
            <PasswordField label="Confirmar senha" autoComplete="new-password" error={form.formState.errors.confirm?.message} {...form.register("confirm")} />
            <Button type="submit" className="mt-2 w-full" loading={accepting}>Criar conta e participar</Button>
          </form>
        )}
      </>
    );
  }

  return (
    <AuthLayout>
      <Panel>
        <h1 className="text-xl font-bold">Participe da oficina</h1>
        {content}
      </Panel>
    </AuthLayout>
  );
}
